import React, { useContext, useEffect, useRef, useState } from "react";
import "./onboardingProgressBar.css";
import { CombinedContext } from "../../../context/combinedContext";
import { ChevronDown, ChevronUp } from "lucide-react";
import billingRequest from "../../../request/billing";
import { useNavigate } from "react-router-dom";
import translations from "../../../translations/translations";

/* ─────────────────────────────────────────
   STEP CONFIG (NO TRANSLATED STRINGS HERE)
────────────────────────────────────────── */
const STEP_CONFIG = {
    shopInfo: {
        id: "info",
        labelKey: "shopInfo",
        page: "/profile",
    },
    schedule: {
        id: "baseSchedule",
        labelKey: "schedule",
        page: "/calendar",
    },
    services: {
        id: "services",
        labelKey: "services",
        page: "/profile",
    },
    images: {
        id: "images",
        labelKey: "images",
        page: "/profile",
    },
    openingHours: {
        id: "hours",
        labelKey: "openingHours",
        page: "/profile",
    },
    contactInfo: {
        id: "contact",
        labelKey: "contactInfo",
        page: "/profile",
    },
    attributes: {
        id: "attributes",
        labelKey: "attributes",
        page: "/profile",
    },
};

const OnboardingProgressBarAdmin = () => {
    const { userData, language } = useContext(CombinedContext);

    const navigate = useNavigate();

    const user = userData?.user;
    const shop = userData?.shop;

    const [showDetails, setShowDetails] = useState(false);
    const [launching, setLaunching] = useState(false);
    const [message, setMessage] = useState(null);
    const [showBubble, setShowbubble] = useState(true);

    const listRef = useRef(null);

    useEffect(() => {
        const el = listRef.current;
        if (!el) return;

        if (showDetails) {
            el.style.height = el.scrollHeight + "px";
            el.style.opacity = "1";
            el.style.transform = "translateY(0)";

            const handleTransitionEnd = () => {
                el.style.height = "auto";
                el.removeEventListener("transitionend", handleTransitionEnd);
            };

            el.addEventListener("transitionend", handleTransitionEnd);
        } else {
            el.style.height = el.scrollHeight + "px";

            requestAnimationFrame(() => {
                el.style.height = "0px";
                el.style.opacity = "0";
                el.style.transform = "translateY(-8px)";
            });
        }
    }, [showDetails]);
    if (!user || user.role !== "owner" || !shop?.setup) return null;

    const steps = shop?.setup?.steps || {};
    const requiredSteps = shop?.setup?.requiredSteps || Object.keys(steps);

    const totalSteps = requiredSteps.length;
    const completedSteps = requiredSteps.filter((step) => steps[step]).length;
    const percentage = Math.round((completedSteps / totalSteps) * 100);
    const allComplete = shop?.setup.isComplete;

    const startBillingSetup = async () => {
        try {
            setLaunching(true);

            const res = await billingRequest.billingSetup({
                shopId: shop.shopId,
                userId: user.userId,
                accessToken: userData.cognitoTokens.AccessToken,
            });

            navigate("/checkout", {
                state: { billingData: res },
            });
        } catch (err) {
            console.error(err);
        } finally {
            setLaunching(false);
        }
    };

    const goToStep = (step) => {
        const stepObj = STEP_CONFIG[step];
        if (!stepObj) return;

        navigate(stepObj.page, {
            state: { openModal: stepObj.id },
        });
    };

    const toggleDetails = () => {
        setShowDetails((prev) => !prev);
        setShowbubble(false);
    };

    return (
        <div>
            <div className="onboard-progress-wrapper">
                {/* HEADER */}
                <div className="onboard-progress-top">
                    <div className="onboard-progress-text">
                        {completedSteps} / {totalSteps}{" "}
                        {translations[language].onboarding.stepsCompleted} ({percentage}%)
                    </div>

                    <button className="onboard-toggle-btn" onClick={toggleDetails}>
                        <span className="onboard-toggle-text">
                            {showDetails
                                ? translations[language].onboarding.hideSteps
                                : translations[language].onboarding.showSteps}
                        </span>

                        <ChevronDown size={16} className={`onboard-chevron ${showDetails ? "rotate" : ""}`} />
                    </button>
                </div>

                {/* PROGRESS BAR */}
                <div className="onboard-progress-bar">
                    <div className="onboard-progress-fill" style={{ width: `${percentage}%` }} />
                </div>

                <ul className={`onboard-steps-list ${showDetails ? "open" : ""}`}>
                    {/* <div className="onboard-steps-inner"> */}
                        {requiredSteps.map((step) => {
                            const labelKey = STEP_CONFIG[step]?.labelKey;
                            const isDone = steps[step];

                            return (
                                <li
                                    key={step}
                                    className={`onboard-step-item ${isDone ? "done" : "pending"}`}
                                    onClick={() => goToStep(step)}
                                >
                                    <span className={`step-indicator ${isDone ? "completed" : "pending"}`}/>

                                    <span className="step-name">
                                        {translations[language].onboarding.steps[labelKey] || step}
                                    </span>
                                </li>
                            );
                        })}
                    {/* </div> */}
                </ul>

                {/* LAUNCH BUTTON */}
                {allComplete && shop.status !== "active" && (
                    <div className="onboard-launch-section">
                        <button
                            type="button"
                            className="onboard-launch-btn"
                            onClick={startBillingSetup}
                            disabled={launching}
                        >
                            {launching
                                ? translations[language].onboarding.launching
                                : translations[language].onboarding.launchShop}
                        </button>

                        {message && <p className={`onboard-msg ${message.type}`}>{message.text}</p>}
                    </div>
                )}
            </div>
            {showBubble && (
                <div className="onboard-overlay-bubble-div">
                    <div className="onboard-overlay-bubble">
                        <div className="onboard-overlay-arrow" />
                        <div className="onboard-overlay-content">
                            <div className="onboard-overlay-title-div">
                                <strong>{translations[language].onboarding?.hintTitle || "Complete your setup"}</strong>
                                <button
                                    className="onboard-overlay-close"
                                    onClick={() => setShowbubble(false)}
                                    aria-label="Close"
                                >
                                    ×
                                </button>
                            </div>
                            <p>
                                {translations[language].onboarding?.hintText ||
                                    "Finish these steps to activate your shop and start accepting bookings."}
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OnboardingProgressBarAdmin;
